/**
 * TanStack Query hooks — Agent Identities (D30 — Chunk 9.M.7).
 *
 * Surface `/settings/agent-identities` super_admin :
 *   - useAgentIdentities({cursor, kind?, include_disabled?, tenant_id?})
 *   - useAgentIdentity(id)
 *   - useCreateAgentIdentity()
 *   - useUpdateAgentDisabled(id)  ← PATCH {disabled: boolean} (soft-disable réversible)
 *
 * RLS Phase 1 :
 *   - GET list/detail : authenticated peut lire (RLS filtre scope tenant + global).
 *   - POST/PATCH : super_admin only (RLS `is_super_admin()`).
 *
 * Pattern parité Brokers/Companies queries.
 */

import type { AgentIdentityRow } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

export type AgentIdentity = AgentIdentityRow;
export type AgentIdentitiesListResult = {
  items: AgentIdentityRow[];
  next_cursor: string | null;
};

export type AgentIdentitiesListParams = {
  cursor?: string | null;
  limit?: number;
  kind?: "ai_operator" | "external_system";
  include_global?: boolean;
  include_disabled?: boolean;
  tenant_id?: string;
};

function buildListPath(params: AgentIdentitiesListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.kind) sp.set("kind", params.kind);
  if (params.tenant_id) sp.set("tenant_id", params.tenant_id);
  if (params.include_global === false) sp.set("include_global", "false");
  if (params.include_disabled === true) sp.set("include_disabled", "true");
  const qs = sp.toString();
  return qs ? `/v1/agent-identities?${qs}` : "/v1/agent-identities";
}

export function useAgentIdentities(params: AgentIdentitiesListParams) {
  return useQuery({
    queryKey: ["agent-identities", "list", params],
    queryFn: () => api.get<AgentIdentitiesListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

export function useAgentIdentity(id: string | undefined) {
  return useQuery({
    queryKey: ["agent-identities", "detail", id],
    queryFn: () => api.get<AgentIdentityRow>(`/v1/agent-identities/${id}`),
    enabled: !!id,
  });
}

export type CreateAgentIdentityPayload = {
  kind: "ai_operator" | "external_system";
  display_name: string;
  description?: string;
  avatar_url?: string;
  /** null = global agent (super_admin only). Sinon tenant scope précis. */
  tenant_id?: string | null;
};

export function useCreateAgentIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAgentIdentityPayload) =>
      api.post<AgentIdentityRow>("/v1/agent-identities", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agent-identities", "list"] });
    },
  });
}

export function useUpdateAgentDisabled(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (disabled: boolean) =>
      api.patch<AgentIdentityRow>(`/v1/agent-identities/${id}`, { disabled }),
    onSuccess: (row) => {
      qc.setQueryData(["agent-identities", "detail", id], row);
      void qc.invalidateQueries({ queryKey: ["agent-identities", "list"] });
    },
  });
}
